import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, X, Package, MessageSquare, FolderKanban, Clock,
  Camera, Fuel, Receipt, CheckCircle, Trash2, Cake, Wrench, ClipboardList, Megaphone
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { notifyBadgesChanged } from '@/lib/moduleBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationPanel({ isOpen, onClose, isMobile = false }) {
  const { language } = useLanguage();
  const { isPreviewMode, viewMode } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchId, setFetchId] = useState(0);

  const fetchNotifications = async (currentFetchId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`);
      // Only update if this is still the current fetch
      setNotifications(prev => {
        // Check if we're still on the same fetch cycle
        return response.data;
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      const newFetchId = Date.now();
      setFetchId(newFetchId);
      fetchNotifications(newFetchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, viewMode?.id]);

  // Plus de lecture automatique (Pavel 2026-08-01) : une notification ne passe
  // en "lu" que par le bouton "Tout lu" ou un clic sur la notification.

  // Notification ouverte en grand (texte complet lisible)
  const [detail, setDetail] = useState(null);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      notifyBadgesChanged();
      const msg = { fr: 'Tout marqué comme lu', ru: 'Всё прочитано', md: 'Tot marcat ca citit', uk: 'Все прочитано' };
      toast.success(msg[language] || msg.fr);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const deleteNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      notifyBadgesChanged();
      const msg = { fr: 'Notification supprimée', ru: 'Уведомление удалено', md: 'Notificare ștearsă', uk: 'Сповіщення видалено' };
      toast.success(msg[language] || msg.fr);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Erreur');
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read if not already. En mode apercu on ne marque rien : le POST
    // ne porte pas view_as et modifierait l'etat de lecture du manager.
    if (!notif.is_read && !isPreviewMode) {
      try {
        await axios.post(`${API}/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
        // La pastille du module concerne s'efface tout de suite, panneau encore ouvert
        notifyBadgesChanged();
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Ouvre la notification en grand : texte complet, relisible a volonte
    // (une annonce se rouvre ainsi comme le pop-up d'origine).
    setDetail({ ...notif, is_read: true });
  };

  const resolveUrl = (notif) => notif.url && notif.url !== '/' ? notif.url : getDefaultUrl(notif.type);

  const goToPage = (notif) => {
    const url = resolveUrl(notif);
    setDetail(null);
    onClose();
    navigate(url);
  };

  const getDefaultUrl = (type) => {
    switch (type) {
      case 'material': return '/materials';
      case 'message': return '/chat';
      case 'project': return '/projects';
      case 'attendance': return '/team-attendance';
      case 'photo': return '/photos';
      case 'fuel': return '/attendance';
      case 'expense': return '/attendance';
      case 'birthday': return '/';
      case 'equipment':
      case 'equipment_overdue':
      case 'equipment_update_reminder': return '/equipment';
      case 'mission': return '/missions';
      default: return '/';
    }
  };

  const getIcon = (type) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case 'material': return <Package className={`${iconClass} text-amber-500`} />;
      case 'message': return <MessageSquare className={`${iconClass} text-blue-500`} />;
      case 'project': return <FolderKanban className={`${iconClass} text-purple-500`} />;
      case 'attendance': return <Clock className={`${iconClass} text-green-500`} />;
      case 'photo': return <Camera className={`${iconClass} text-pink-500`} />;
      case 'fuel': return <Fuel className={`${iconClass} text-orange-500`} />;
      case 'expense': return <Receipt className={`${iconClass} text-teal-500`} />;
      case 'birthday': return <Cake className={`${iconClass} text-pink-500`} />;
      case 'equipment':
      case 'equipment_overdue':
      case 'equipment_update_reminder': return <Wrench className={`${iconClass} text-slate-500`} />;
      case 'mission': return <ClipboardList className={`${iconClass} text-indigo-500`} />;
      case 'bonus':
      case 'penalty':
      case 'advance': return <Receipt className={`${iconClass} text-emerald-500`} />;
      case 'announcement': return <Megaphone className={`${iconClass} text-blue-600`} />;
      default: return <Bell className={`${iconClass} text-gray-500`} />;
    }
  };

  const getTypeBgColor = (type, isRead) => {
    if (isRead) return 'bg-gray-50';
    switch (type) {
      case 'material': return 'bg-amber-100';
      case 'message': return 'bg-blue-100';
      case 'project': return 'bg-purple-100';
      case 'attendance': return 'bg-green-100';
      case 'photo': return 'bg-pink-100';
      case 'fuel': return 'bg-orange-100';
      case 'expense': return 'bg-teal-100';
      case 'birthday': return 'bg-pink-100';
      case 'announcement': return 'bg-blue-100';
      default: return 'bg-gray-100';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      fr: {
        material: 'Matériau',
        message: 'Message',
        project: 'Chantier',
        attendance: 'Pointage',
        photo: 'Photo',
        fuel: 'Carburant',
        expense: 'Dépense',
        birthday: 'Anniversaire',
        mission: 'Mission',
        bonus: 'Prime',
        penalty: 'Pénalité',
        advance: 'Acompte',
        user: 'Utilisateur',
        equipment: 'Équipement',
        equipment_overdue: 'Entretien dépassé',
        equipment_update_reminder: 'Équipement',
        announcement: 'Annonce'
      },
      ru: {
        material: 'Материал',
        message: 'Сообщение',
        project: 'Проект',
        attendance: 'Посещаемость',
        photo: 'Фото',
        fuel: 'Топливо',
        expense: 'Расход',
        birthday: 'День рождения',
        mission: 'Задание',
        bonus: 'Премия',
        penalty: 'Штраф',
        advance: 'Аванс',
        user: 'Пользователь',
        equipment: 'Оборудование',
        equipment_overdue: 'ТО просрочено',
        equipment_update_reminder: 'Оборудование',
        announcement: 'Объявление'
      },
      md: {
        material: 'Material',
        message: 'Mesaj',
        project: 'Proiect',
        attendance: 'Prezență',
        photo: 'Foto',
        fuel: 'Combustibil',
        expense: 'Cheltuială',
        birthday: 'Zi de naștere',
        mission: 'Misiune',
        bonus: 'Primă',
        penalty: 'Penalizare',
        advance: 'Avans',
        user: 'Utilizator',
        equipment: 'Echipament',
        equipment_overdue: 'Întreținere depășită',
        equipment_update_reminder: 'Echipament',
        announcement: 'Anunț'
      },
      uk: {
        material: 'Матеріал',
        message: 'Повідомлення',
        project: 'Об\'єкт',
        attendance: 'Відвідування',
        photo: 'Фото',
        fuel: 'Паливо',
        expense: 'Витрата',
        birthday: 'День народження',
        mission: 'Завдання',
        bonus: 'Премія',
        penalty: 'Штраф',
        advance: 'Аванс',
        user: 'Користувач',
        equipment: 'Обладнання',
        equipment_overdue: 'ТО прострочено',
        equipment_update_reminder: 'Обладнання',
        announcement: 'Оголошення'
      }
    };
    return labels[language]?.[type] || labels.fr[type] || type;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) {
      const justNow = { fr: 'À l\'instant', ru: 'Только что', md: 'Chiar acum', uk: 'Щойно' };
      return justNow[language] || justNow.fr;
    }
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    const locales = { fr: 'fr-FR', ru: 'ru-RU', md: 'ro-RO', uk: 'uk-UA' };
    const loc = locales[language] || 'fr-FR';
    return `${date.toLocaleDateString(loc)} ${date.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })}`;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (!isOpen) return null;

  const NotificationItem = ({ notif }) => (
    <div
      onClick={() => handleNotificationClick(notif)}
      className={`p-3 hover:bg-gray-100 cursor-pointer transition-colors border-b last:border-b-0 ${
        notif.is_read ? 'opacity-60' : ''
      }`}
      data-testid={`notification-item-${notif.id}`}
    >
      <div className="flex gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getTypeBgColor(notif.type, notif.is_read)}`}>
          {getIcon(notif.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              notif.is_read ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'
            }`}>
              {getTypeLabel(notif.type)}
            </span>
            <span className="text-xs text-gray-400">{formatTime(notif.created_at)}</span>
            {!notif.is_read && (
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </div>
          <h4 className={`font-semibold text-sm ${notif.is_read ? 'text-gray-500' : 'text-gray-800'}`}>
            {notif.title_i18n?.[language] || notif.title}
          </h4>
          <p className={`text-sm line-clamp-2 ${notif.is_read ? 'text-gray-400' : 'text-gray-600'}`}>
            {notif.message_i18n?.[language] || notif.message}
          </p>
        </div>
        {!isPreviewMode && (
          <button
            onClick={(e) => deleteNotification(notif.id, e)}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
            title={{ fr: 'Supprimer', ru: 'Удалить', md: 'Șterge', uk: 'Видалити' }[language] || 'Supprimer'}
            data-testid={`delete-notification-${notif.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  // Notification en grand : titre + texte complet + acces a la page liee.
  // C'est aussi ce qui permet de RELIRE une annonce apres son pop-up.
  const L = {
    open: { fr: 'Ouvrir la page', ru: 'Открыть страницу', md: 'Deschide pagina', uk: 'Відкрити сторінку' },
    close: { fr: 'Fermer', ru: 'Закрыть', md: 'Închide', uk: 'Закрити' },
  };
  const detailModal = detail ? (
    <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            {getIcon(detail.type)}
            <span className="flex-1 text-left leading-snug">{detail.title_i18n?.[language] || detail.title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">{getTypeLabel(detail.type)}</span>
            <span className="text-gray-400">{formatTime(detail.created_at)}</span>
          </div>
          <p className="whitespace-pre-line text-sm text-gray-700 leading-relaxed max-h-[50vh] overflow-y-auto">
            {detail.message_i18n?.[language] || detail.message}
          </p>
          <div className="flex gap-2 pt-1">
            {resolveUrl(detail) !== '/' && (
              <Button onClick={() => goToPage(detail)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                {L.open[language] || L.open.fr}
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)} className="flex-1">
              {L.close[language] || L.close.fr}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;

  // Mobile version - inline panel
  if (isMobile) {
    return (
      <div className="max-h-[60vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50">
          <h3 className="font-bold text-gray-800">
            {{ fr: 'Notifications', ru: 'Уведомления', md: 'Notificări', uk: 'Сповіщення' }[language] || 'Notifications'}
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">{unreadCount}</span>
            )}
            {isPreviewMode && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                {viewMode?.name}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && !isPreviewMode && (
              <button
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                <CheckCircle className="w-3 h-3" />
                {{ fr: 'Tout lu', ru: 'Прочитано', md: 'Citit', uk: 'Прочитано' }[language] || 'Tout lu'}
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-sm">
                {{ fr: 'Aucune notification', ru: 'Нет уведомлений', md: 'Fără notificări', uk: 'Немає сповіщень' }[language] || 'Aucune notification'}
              </p>
            </div>
          ) : (
            notifications.map(notif => (
              <NotificationItem key={notif.id} notif={notif} />
            ))
          )}
        </div>
        {detailModal}
      </div>
    );
  }

  // Desktop version - dropdown panel
  return (
    <>
      {/* Backdrop - only covers area outside sidebar */}
      <div 
        className="fixed inset-0 left-[260px] z-40" 
        onClick={onClose}
      />
      
      {/* Panel - positioned to the right of sidebar */}
      <div className="fixed left-[260px] top-4 w-96 max-w-[calc(100vw-280px)] bg-white rounded-xl shadow-2xl border z-50 overflow-hidden animate-slideIn">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50">
          <h3 className="font-bold text-gray-800">
            {{ fr: 'Notifications', ru: 'Уведомления', md: 'Notificări', uk: 'Сповіщення' }[language] || 'Notifications'}
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">{unreadCount}</span>
            )}
            {isPreviewMode && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                {viewMode?.name}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && !isPreviewMode && (
              <button
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                <CheckCircle className="w-3 h-3" />
                {{ fr: 'Tout lu', ru: 'Прочитано', md: 'Citit', uk: 'Прочитано' }[language] || 'Tout lu'}
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-sm">
                {{ fr: 'Aucune notification', ru: 'Нет уведомлений', md: 'Fără notificări', uk: 'Немає сповіщень' }[language] || 'Aucune notification'}
              </p>
            </div>
          ) : (
            notifications.map(notif => (
              <NotificationItem key={notif.id} notif={notif} />
            ))
          )}
        </div>
        
        {/* Footer with count */}
        {notifications.length > 0 && (
          <div className="p-2 border-t bg-gray-50 text-center">
            <span className="text-xs text-gray-500">
              {{ fr: `${notifications.length} notification${notifications.length > 1 ? 's' : ''}`, 
                 ru: `${notifications.length} уведомлен${notifications.length === 1 ? 'ие' : 'ий'}`,
                 md: `${notifications.length} notificăr${notifications.length === 1 ? 'e' : 'i'}`,
                 uk: `${notifications.length} сповіщен${notifications.length === 1 ? 'ня' : 'ь'}`
              }[language] || `${notifications.length} notification${notifications.length > 1 ? 's' : ''}`}
            </span>
          </div>
        )}
      </div>

      {detailModal}
    </>
  );
}
